import { useSpritzCardQuery } from '../../graphql/generated/graphql'
import { ScriptStatus, useScript } from '../../utils/useScript'
import React, { useEffect, useRef, useState } from 'react'
import { isProduction } from '../app/config'
import whiteLogo from '../../assets/img/white-logo.svg'
import styled, { css } from 'styled-components'
import { breakpoints } from '@spritz-finance/ui'
import SpritzCardFrontRawImage from '../../assets/img/spritz-card-front.svg'
import ShowImage from '../../assets/icons/show.svg'
import HideImage from '../../assets/icons/hide.svg'

declare global {
    interface Window {
        CheckbookVccDetails: (args: { env: string; card_id: string; signature: string }) => {
            render: (string) => void
        }
    }
}

const scriptUrl = isProduction()
    ? 'https://app.checkbook.io/embed/vcc.js'
    : 'https://sandbox.checkbook.io/embed/vcc.js'

const CardOuterWrapper = styled.div`
    position: relative;
`

const CardWrapper = styled.div`
    width: auto;
    height: 300px;
    position: relative;
    overflow: hidden;
    & > div {
        height: 100%;
        width: 100%;
    }

    ${breakpoints.xsmall} {
        width: auto;
        height: 215px;
    }
`

export const Logo = styled.img`
    top: 24px;
    left: 32px;
    width: 108px;
    position: absolute;
    z-index: 2;
`

const SceneWrapper = styled.div<{ rotated: boolean }>`
    width: auto;
    height: 290px;
    perspective: 600px;
    margin-bottom: 24px;

    .card {
        width: 100%;
        height: 100%;
        position: relative;
        transition: transform 0.5s;
        transform-style: preserve-3d;
        ${({ rotated }) =>
            rotated &&
            css`
                transform: rotateY(180deg);
            `}
    }

    .face {
        width: 100%;
        height: 100%;

        color: #fff;
        line-height: 15em;
        text-align: center;
        position: absolute;

        backface-visibility: hidden;
    }

    .front {
        border-radius: 18px;
    }

    .back {
        transform: rotateY(180deg);
    }

    ${breakpoints.xsmall} {
        width: auto;
        height: 215px;
    }
`

export const SpritzCardIFrame: React.VFC<{ toggle: () => void }> = ({ toggle }) => {
    const { data } = useSpritzCardQuery()
    const status = useScript(scriptUrl)

    const vccContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!(vccContainerRef.current && data?.virtualCard && status === ScriptStatus.ready)) {
            return
        }

        window
            .CheckbookVccDetails({
                env: isProduction() ? 'PROD' : 'SANDBOX',
                card_id: data.virtualCard.externalId,
                signature: data.virtualCard.renderSecret,
            })
            .render('#container')
    }, [vccContainerRef, data, status])

    return (
        <CardOuterWrapper>
            <Logo src={whiteLogo} />
            <CardWrapper id="container" ref={vccContainerRef} />
            <OverlayedDataRow>
                <HideIcon onClick={toggle} />
            </OverlayedDataRow>
        </CardOuterWrapper>
    )
}

const SpritzCardFrontWrapper = styled.div``

const FrontImage = styled.img.attrs({
    src: SpritzCardFrontRawImage,
})`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0px;
    left: 0px;
    z-index: 1;
`

const BaseIcon = css`
    cursor: pointer;
    width: 24px;
    height: 24px;
    z-index: 2;
    margin-right: 48px;
    margin-left: 36px;

    ${breakpoints.xsmall} {
        margin-right: 24px;
        margin-left: 18px;
    }
`

const ShowIcon = styled.img.attrs({
    src: ShowImage,
})`
    ${BaseIcon}
`

const HideIcon = styled.img.attrs({
    src: HideImage,
})`
    ${BaseIcon}
`

const MaskNumbers = styled.div`
    color: white;
    font-size: 25px;
    line-height: 40px;
    ${breakpoints.xsmall} {
        font-size: 16px;
    }
`

const OverlayedDataRow = styled.div`
    position: absolute;
    top: 170px;
    width: 100%;
    z-index: 2;
    height: 40px;
    display: flex;
    flex-direction: row;
    justify-content: end;
    align-items: center;
    ${breakpoints.xsmall} {
        top: 125px;
        height: 20px;
    }
`

export const SpritzCardFront: React.VFC<{ toggle: () => void }> = ({ toggle }) => {
    const { data } = useSpritzCardQuery()

    return (
        <SpritzCardFrontWrapper>
            <FrontImage />
            <OverlayedDataRow>
                <MaskNumbers>{data?.virtualCard?.mask}</MaskNumbers>
                <ShowIcon onClick={toggle} />
            </OverlayedDataRow>
        </SpritzCardFrontWrapper>
    )
}

export const SpritzCardScene = () => {
    const [rotated, setRotated] = useState(false)
    const toggle = () => setRotated(!rotated)

    return (
        <SceneWrapper className="scene" rotated={rotated}>
            <div className="card">
                <div className="face front" style={{ zIndex: rotated ? 100 : 200 }}>
                    <SpritzCardFront toggle={toggle} />
                </div>
                <div className="face back" style={{ zIndex: rotated ? 200 : 100 }}>
                    <SpritzCardIFrame toggle={toggle} />
                </div>
            </div>
        </SceneWrapper>
    )
}
